import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestIdleCallback, requestYieldCallback } from "@theme/utilities";

/**
 * Global cache shared across all variant picker instances
 * This allows cache to persist when switching between products in combined listings
 *
 * HYBRID APPROACH:
 * - Cache preloaded variants for instant switching (no network delay)
 * - Fetch lightweight JSON for uncached variants (faster than full HTML)
 * - Fallback to full HTML when variant ID not available
 */
const globalVariantsCache = new Map();
// productId → 옵션값 ID 맵([{id, a, m, v:[optionValueId,...]}]), locale 무관 variant 해석용.
// 옵션값 문자열(option1..3)은 번역 가능 리소스라 매칭 키로 신뢰할 수 없다.
const globalOptionMapCache = new Map();

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {AbortController | undefined} 경량 JSON fetch 전용. HTML fetch 와 컨트롤러를 나눈다(2026-09-16) */
  #jsonAbortController;

  /** @type {string | undefined} */
  #lastProductId;
  /** change 리스너 해제용. bind 한 함수는 remove 로 못 지운다. */
  #changeController = new AbortController();


  connectedCallback() {
    super.connectedCallback();

    // Store initial product ID
    this.#lastProductId = this.dataset.productId;

    // HYBRID: Load and cache preloaded variants for instant switching
    this.#loadVariantsCache();

    // bind 는 매번 새 함수를 만들어 remove 가 안 먹었다. signal 로 해제한다.
    this.addEventListener("change", this.variantChanged.bind(this), {
      signal: this.#changeController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Abort any pending fetch requests to prevent memory leaks
    this.#abortController?.abort();
    this.#jsonAbortController?.abort();
    this.#changeController.abort();
    this.#changeController = new AbortController();
  }

  updatedCallback() {
    super.updatedCallback?.();

    // When product changes (combine listing), re-load cache for new product
    const currentProductId = this.dataset.productId;
    if (currentProductId !== this.#lastProductId) {
      this.#lastProductId = currentProductId;
      // HYBRID: Re-load cache for new product
      this.#loadVariantsCache();
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    // 다른 축의 가용성 표시를 새 선택 기준으로 갱신한다(색 변경 -> 사이즈 품절 표시).
    this.#refreshOptionAvailability();
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // HYBRID APPROACH: Cache first, then JSON fetch, then full HTML fallback
    const cachedVariant = this.#getCachedVariant(selectedOption);

    // 이 선택의 정식 variant id (fetch·URL용 단일 진실).
    // - 완결 선택: 옵션값 튜플 정확매칭 id. 클릭된 옵션의 대표 variant
    //   (selectedOption.dataset.variantId)는 다른 컬러일 수 있어 신뢰 불가
    //   (F-VP1 wrong-variant: 존재하지 않는 컬러+사이즈 클릭 시 엉뚱한 컬러가 커밋됨).
    // - 불완전 선택(컬러만·사이즈 deselect): 클릭된 옵션의 대표 variant로 컬러 미리보기.
    const selectionComplete = this.isSelectionComplete;
    const variantId = selectionComplete
      ? this.#exactVariantIdForSelection()
      : selectedOption.dataset.variantId;

    if (cachedVariant) {
      // ⚡ CACHE HIT: Instant update with no network delay
      this.#updateCriticalUI(cachedVariant, true);

      // Detect if product changed (for combine listings in quick-add drawer, product cards, etc.)
      const newProductUrl = selectedOption.dataset.connectedProductUrl;
      const currentProductUrl = this.dataset.productUrl?.split("?")[0];
      const hasProductChanged = newProductUrl && currentProductUrl !== newProductUrl;

      // Dispatch event immediately for other components
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(cachedVariant, this.selectedOptionId, {
            html: null, // No HTML yet
            productId: this.dataset.productId ?? "",
            newProduct: hasProductChanged ? { id: null, url: newProductUrl } : undefined,
            fromCache: true, // Flag to indicate this is from cache
            ...this.#selectionStateData(),
          })
        );
      }

      // Still fetch HTML in background for morph (custom elements, etc.)
      requestIdleCallback(() => {
        this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, true);
      });
    } else if (variantId) {
      // CACHE MISS: Fetch lightweight JSON (faster than full HTML)
      // Detect if product changed (for combine listings in quick-add drawer, product cards, etc.)
      const newProductUrl = selectedOption.dataset.connectedProductUrl;
      const currentProductUrl = this.dataset.productUrl?.split("?")[0];
      const hasProductChanged = newProductUrl && currentProductUrl !== newProductUrl;

      this.#fetchVariantJson(variantId)
        .then((variantData) => {
          if (variantData) {
            // Update UI with fetched JSON data
            this.#updateCriticalUI(variantData, true);

            // Dispatch event immediately for other components
            if (this.selectedOptionId) {
              this.dispatchEvent(
                new VariantUpdateEvent(variantData, this.selectedOptionId, {
                  html: null, // No HTML yet
                  productId: this.dataset.productId ?? "",
                  newProduct: hasProductChanged ? { id: null, url: newProductUrl } : undefined,
                  fromCache: true, // Treated as cache hit for UI logic
                  ...this.#selectionStateData(),
                })
              );
            }
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch variant JSON:", error);
        });

      // Fetch full HTML in background for morph (custom elements, etc.)
      requestIdleCallback(() => {
        this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, true);
      });
    } else if (selectionComplete) {
      // 완결인데 정확매칭 variant 없음 = 존재하지 않는 조합(예: 사이즈 M이 다른
      // 컬러에만 존재). 대표 variant(selectedOption.dataset.variantId) 폴백은 wrong
      // color를 커밋하므로, resource=null로 "Unavailable"을 즉시 dispatch한다
      // product-form #onVariantUpdate이 변형 id를 비우고 버튼을 비활성화한다(F-VP1).
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(null, this.selectedOptionId, {
            html: null,
            productId: this.dataset.productId ?? "",
            fromCache: true,
            ...this.#selectionStateData(),
          })
        );
      }
    } else {
      // FALLBACK: No variant ID - fetch full HTML as fallback
      this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct, false);
    }

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest("quick-add-dialog") || this.closest("swatches-variant-picker")) {
      let sectionId = this.quickAddDrawerId;
      if (this.closest("swatches-variant-picker")) {
        sectionId = "section-rendering-product-card";
      }
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=${sectionId}&${params.join("&")}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {boolean} isBackgroundSync - If this is a background sync after cache hit (don't dispatch events again)
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false, isBackgroundSync = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, "text/html");
        const variantData = this.#extractVariantData(html);

        if (variantData) {
          if (!isBackgroundSync) {
            // Update critical UI immediately (only if not from cache)
            // Note: Media is NOT updated here - will be replaced via HTML morph
            this.#updateCriticalUI(variantData, false);
          }
        } else {
          console.warn("No variant data extracted - dispatching without optimistic update");
        }

        // Morph HTML (will happen after critical UI update)
        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Always dispatch event with HTML (even without variantData or for background sync)
          // Components need HTML to morph elements (button text, inventory, badge)
          // 선택 상태 플래그는 morph 이후의 라이브 DOM 기준으로 계산
          // form은 data-skip-subtree-update라 클라이언트 선택 상태(deselect 포함)가 보존된다
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData || null, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
                fromCache: false, // Explicitly set to false for HTML fetch
                isBackgroundSync, // Flag to indicate this is background sync
                ...this.#selectionStateData(),
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Fetch lightweight JSON data for a specific variant
   * This is faster than fetching full HTML (smaller payload)
   *
   * @param {string} variantId - The variant ID to fetch
   * @returns {Promise<Object|null>} The variant data or null
   */
  async #fetchVariantJson(variantId) {
    if (!variantId) return null;

    try {
      // Build URL to fetch variant-data section
      const productUrl = this.dataset.productUrl || window.location.pathname;
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("sections", "variant-data");
      url.searchParams.set("variant", variantId);

      // Abort previous JSON fetch if still pending (rapid clicking).
      // 2026-09-16: HTML fetch(fetchUpdatedSection) 와 다른 컨트롤러를 쓴다. 같이 쓰면 백그라운드
      // HTML fetch 가 이 JSON fetch 를 즉시 취소해 가격·미디어 낙관 업데이트가 사라졌다.
      this.#jsonAbortController?.abort();
      this.#jsonAbortController = new AbortController();

      // Fetch section (Shopify wraps in HTML, not pure JSON)
      const response = await fetch(url.toString(), { signal: this.#jsonAbortController.signal });

      if (!response.ok) {
        console.warn(`Variant JSON fetch failed: ${response.status}`);
        return null;
      }

      // Section Rendering API returns: { "variant-data": "HTML string" }
      const data = await response.json();
      const htmlString = data["variant-data"];

      if (!htmlString) {
        console.warn("No variant-data in response");
        return null;
      }

      // Parse HTML to extract JSON from section content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlString;
      const sectionDiv = tempDiv.querySelector("#shopify-section-variant-data");

      if (!sectionDiv?.textContent) {
        console.warn("Could not extract JSON from section");
        return null;
      }

      // Parse the JSON from section content
      const variantData = JSON.parse(sectionDiv.textContent);

      return variantData;
    } catch (error) {
      // Silent abort errors (user clicked another variant)
      if (error.name === "AbortError") return null;

      console.error("Error fetching variant JSON:", error);
      return null;
    }
  }

  /**
   * Extract variant data from HTML response
   * @param {Document} html - The parsed HTML document
   * @returns {Object | null} The variant data or null if not found
   */
  #extractVariantData(html) {
    // 익명 JSON(selected variant)만, 캐시 스크립트(data-variants-cache)가
    // 마크업상 앞에 오더라도 오파싱하지 않도록 명시 제외 (순서 계약 제거)
    const scriptTag = html.querySelector('variant-picker script[type="application/json"]:not([data-variants-cache])');

    if (!scriptTag?.textContent) {
      console.warn("No variant data found in response");
      return null;
    }

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error("Failed to parse variant data:", e);
      return null;
    }
  }

  /**
   * Update critical UI elements immediately with variant data
   * @param {Object} variantData - The variant object from JSON
   * @param {boolean} isFromCache - Whether this update is from cache (true) or from server (false)
   */
  #updateCriticalUI(variantData, isFromCache = false) {
    // 1. Update price immediately
    this.#updatePriceOptimistic(variantData);

    // 2. Update media gallery (only for cache, server fetch will replace entire gallery)
    if (isFromCache) {
      this.#updateMediaOptimistic(variantData);
    }

    // 3. Update button state
    this.#updateButtonStateOptimistic(variantData);
  }

  /**
   * Update price optimistically from variant data
   * @param {Object} variant - The variant object
   */
  #updatePriceOptimistic(variant) {
    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const priceComponent = scope?.querySelector("product-price");

    if (!priceComponent) return;

    // Dispatch optimistic update event
    priceComponent.dispatchEvent(
      new CustomEvent("price:update-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update media gallery optimistically
   * @param {Object} variant - The variant object
   */
  #updateMediaOptimistic(variant) {
    if (!variant.featured_media) return;

    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const mediaGallery = scope?.querySelector("media-gallery");

    if (!mediaGallery) return;

    // Dispatch optimistic media switch event
    mediaGallery.dispatchEvent(
      new CustomEvent("media:switch-optimistic", {
        detail: { variant },
        bubbles: false,
      })
    );
  }

  /**
   * Update button state optimistically
   * @param {Object} variant - The variant object
   */
  #updateButtonStateOptimistic(variant) {
    // 선택 미완결(KS: 사이즈 미선택)이면 대표 variant의 가용성으로 버튼을 칠하지 않는다.
    // 버튼 상태는 product-form이 VariantUpdateEvent의 selectionComplete/anyAvailable로 결정.
    if (!this.isSelectionComplete) return;

    // IMPORTANT: Scope to product-card first to avoid affecting other cards in the same section
    const scope = this.closest("product-card") || this.closest(".shopify-section, dialog");
    const productForm = scope?.querySelector("product-form-component");

    if (!productForm) return;

    const addToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    if (!addToCartButton) return;

    // PFAS(2026-09-16): 미국 판매불가 상품은 variant 가용성과 무관하게 잠긴 채 둔다.
    // product-form.enable() 의 같은 가드를 여기서 직접 대입으로 우회하던 경로(P1) 차단.
    if (addToCartButton.dataset.pfasUsBlocked === "true") return;

    // Update button state based on availability
    addToCartButton.disabled = !variant.available;
  }

  /**
   * 선택 완결성, "사용자가 모든 옵션 축을 명시적으로 선택했는가"의 단일 진실 공급원.
   * 모든 라디오 옵션 그룹(.variant-option--fieldset)에 checked 입력이 있어야 완결.
   * select 드롭다운은 항상 선택값이 있으므로 완결로 간주, 그룹이 없으면(기본 variant 상품) 완결.
   * KS 인라인 스크립트(variant-main-picker)가 사이즈 라디오를 deselect하면 false가 되고,
   * 소비자(product-form 등)는 VariantUpdateEvent의 data.selectionComplete로 이 상태를 전달받는다.
   * @returns {boolean}
   */
  get isSelectionComplete() {
    const groups = Array.from(this.querySelectorAll(".variant-option--fieldset"));
    return groups.every((group) => group.querySelector("input:checked"));
  }

  /**
   * 옵션 맵(productId → 옵션값 ID row 배열) 또는 null. 옵션값 ID 매칭의 단일 데이터.
   * @returns {Array<{id:(number|string), a:boolean, im?:boolean, v:Array<number|string>}> | null}
   */
  #optionMap() {
    const productId = this.dataset.productId;
    return (productId && globalOptionMapCache.get(productId)) || null;
  }

  /**
   * 현재(부분) 선택을 {position: optionValueId}로 수집, 옵션 매칭의 공통 전처리.
   * contractOk=false면 생산자 계약(data-option-position/value-id) 미충족 →
   * 호출부가 degrade(fail-open 등)를 판단한다. count는 선택 입력 수.
   * @returns {{byPosition: Record<number,string>, contractOk: boolean, count: number}}
   */
  #selectedByPosition() {
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );
    /** @type {Record<number, string>} */
    const byPosition = {};
    for (const el of selectedOptions) {
      const position = parseInt(el.dataset.optionPosition || "", 10);
      const valueId = el.dataset.optionValueId;
      if (!position || !valueId) return { byPosition: {}, contractOk: false, count: selectedOptions.length };
      byPosition[position] = valueId;
    }
    return { byPosition, contractOk: true, count: selectedOptions.length };
  }

  /**
   * map row가 현재 선택과 매칭되는가, 미선택 position은 와일드카드로 취급(부분 선택).
   * @param {{v: Array<number|string>}} row
   * @param {Record<number,string>} byPosition
   */
  #rowMatchesSelection(row, byPosition) {
    return row.v.every((id, idx) => {
      const selected = byPosition[idx + 1];
      return selected == null || String(selected) === String(id);
    });
  }

  /**
   * map row가 현재 선택과 완전 튜플로 정확매칭되는가, 미선택 position은 불일치 처리.
   * @param {{v: Array<number|string>}} row
   * @param {Record<number,string>} byPosition
   */
  #rowMatchesExact(row, byPosition) {
    return row.v.every((id, idx) => String(byPosition[idx + 1] ?? "") === String(id));
  }

  /**
   * 각 옵션 입력의 가용성을 현재 선택 기준으로 다시 계산해 DOM 에 반영한다 (F-VP2).
   *
   * 왜 필요한가: `data-option-available` 과 `aria-disabled` 는 Liquid 가 첫 렌더에
   * 한 번만 그린다. 폼은 `data-skip-subtree-update` 라 morph 대상에서도 빠지므로
   * 색을 바꿔도 사이즈의 가용성 표시가 첫 렌더 값에 머물렀다.
   * 결과가 양방향으로 나쁘다. 새 색에서 파는 사이즈가 품절로 보여 판매를 잃고,
   * 안 파는 사이즈가 살 수 있는 것처럼 보여 담기에서 실패한다.
   *
   * 계산: 자기 축은 후보값으로 고정하고 나머지 축은 현재 선택을 그대로 둔 뒤
   * 캐시에서 구매 가능한 row 가 하나라도 있는지 본다. 미선택 축은 와일드카드다.
   *
   * 계약: 캐시가 없거나 계약을 못 지키면 **아무것도 바꾸지 않는다**(첫 렌더 유지).
   * degrade 는 전체 노출이고 fail-closed 로 구매를 막지 않는다는 기존 방침과 같다.
   */
  #refreshOptionAvailability() {
    const optionMap = this.#optionMap();
    if (!optionMap) return;

    const { byPosition, contractOk } = this.#selectedByPosition();
    if (!contractOk) return;

    const inputs = this.querySelectorAll("input[data-option-position][data-option-value-id]");
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const position = parseInt(input.dataset.optionPosition || "", 10);
      const valueId = input.dataset.optionValueId;
      if (!position || !valueId) continue;

      const probe = { ...byPosition, [position]: valueId };
      let available = false;
      for (const row of optionMap) {
        if (!this.#rowMatchesSelection(row, probe)) continue;
        if (row.a) {
          available = true;
          break;
        }
      }
      input.dataset.optionAvailable = String(available);
      if (available) input.removeAttribute("aria-disabled");
      else input.setAttribute("aria-disabled", "true");
    }
  }

  /**
   * 현재(부분) 선택 범위의 가용성 집계, 매칭 row를 한 번만 순회한다.
   * - anyAvailable: 구매 가능 조합 존재 여부. map 없음/계약 미충족이면 판단 불가 →
   *   fail-open(true). 계약: degrade = 전체 노출(fail-closed로 구매를 막지 않는다).
   * - anySoldOut: 구매 불가가 sold out(재고추적 im===true)인지 vs unavailable인지.
   *   매칭 row 중 unavailable+재고추적이 하나라도 있으면 true, 전부 비추적이면 false.
   *   im 부재(구버전 캐시)·map 없음·계약 미충족이면 undefined → 소비자 기본값 유지.
   * @returns {{anyAvailable: boolean, anySoldOut: (boolean|undefined)}}
   */
  #availabilityForSelection() {
    const optionMap = this.#optionMap();
    if (!optionMap) return { anyAvailable: true, anySoldOut: undefined };

    const { byPosition, contractOk } = this.#selectedByPosition();
    if (!contractOk) return { anyAvailable: true, anySoldOut: undefined };

    let anyAvailable = false;
    let sawIm = false;
    let anyManaged = false;
    for (const row of optionMap) {
      if (!this.#rowMatchesSelection(row, byPosition)) continue;
      if (row.a) anyAvailable = true;
      if (typeof row.im !== "undefined") sawIm = true;
      if (!row.a && row.im === true) anyManaged = true;
    }
    return { anyAvailable, anySoldOut: sawIm ? anyManaged : undefined };
  }

  /**
   * VariantUpdateEvent에 싣는 선택 상태 데이터 (dispatch 시점의 라이브 DOM 기준).
   * @returns {{selectionComplete: boolean, anyAvailable: boolean, anySoldOut: (boolean|undefined)}}
   */
  #selectionStateData() {
    return {
      selectionComplete: this.isSelectionComplete,
      ...this.#availabilityForSelection(),
    };
  }

  /**
   * 현재 선택을 옵션값 튜플 정확매칭으로 해석한 variant id.
   * 모든 옵션 position이 선택값으로 채워진 완전 튜플만 매칭(와일드카드 없음)
   * 미선택 position이 있거나 매칭 row가 없으면 null. map 없음/계약 미충족도 null.
   * 완결 선택의 fetch·URL·submit 커밋용 단일 진실. 옵션값 대표 variant와 달리
   * 다른 컬러로 새지 않는다(F-VP1).
   * @returns {number|string|null}
   */
  #exactVariantIdForSelection() {
    const optionMap = this.#optionMap();
    if (!optionMap) return null;

    const { byPosition, contractOk, count } = this.#selectedByPosition();
    if (!contractOk || count === 0) return null;

    const row = optionMap.find((r) => this.#rowMatchesExact(r, byPosition));
    return row ? row.id : null;
  }

  /**
   * 현재 선택의 정식 variant id (옵션값 튜플 정확매칭). 완결 선택이 아니거나
   * 매칭 불가/맵 없음이면 null. product-form이 submit 직전 form 동기화(stale 방어,
   * F-PF7)에 쓴다.
   * @returns {number|string|null}
   */
  get resolvedVariantId() {
    return this.#exactVariantIdForSelection();
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    if (!newHtml) return;

    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], .variant-option--fieldset input:checked");

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], .variant-option--fieldset input:checked")
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  get quickAddDrawerId() {
    const quickAddDrawer = document.getElementById("quick-add-drawer");
    return quickAddDrawer?.dataset.sectionId;
  }

  /**
   * HYBRID APPROACH: Load and cache preloaded variants for instant switching
   * Variants are preloaded in the initial HTML for zero-latency updates
   */
  #loadVariantsCache() {
    const cacheScript = this.querySelector("script[data-variants-cache]");

    if (!cacheScript?.textContent) {
      console.warn("⚠️ No variants cache found - will fetch from server");
      return;
    }

    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId found - cannot cache variants");
      return;
    }

    try {
      const data = JSON.parse(cacheScript.textContent);

      // Cache variants indexed by productId + variant id (locale 무관 조회 키)
      if (data.variants && Array.isArray(data.variants)) {
        data.variants.forEach((variant) => {
          if (!variant?.id) return; // 대표 variant 없는 옵션값은 null로 내려올 수 있음 → skip
          globalVariantsCache.set(`${productId}|id:${variant.id}`, variant);
        });
      }

      // 옵션값 ID 맵 적재, #getCachedVariant의 해석 경로. 유효성은 생산자
      // (ks-variants-cache)가 보장하며 무효/lite면 map:null로 내려온다.
      // 맵이 없으면 캐시 미스로 동작해 variant-data JSON fetch 경로로 내려간다.
      if (Array.isArray(data.map) && data.map.length > 0) {
        globalOptionMapCache.set(productId, data.map);
      }

    } catch (e) {
      console.error("Failed to load variants cache:", e);
    }
  }

  /**
   * 캐시에서 variant 해석, 옵션값 ID 매칭 (locale 무관, 번역된 옵션값 문자열 미사용)
   * 1) 전 옵션이 선택돼 있으면 옵션값 ID 튜플 × map으로 정확 매칭.
   * 2) 선택이 불완전하면(KS 기본: 컬러 클릭 시 사이즈 deselect, 카드는 컬러 그룹만 렌더)
   *    클릭된 옵션의 data-variant-id(해당 옵션값의 대표 variant)로 직조회, 캐시 미스
   *    fetch 경로(#fetchVariantJson)가 쓰는 것과 동일한 variant라 의미 동등.
   * 둘 다 실패하면 null → 호출부가 variant-data JSON fetch로 처리.
   * @param {HTMLElement} [selectedOption] - 방금 변경된 옵션 요소
   */
  #getCachedVariant(selectedOption) {
    const productId = this.dataset.productId;
    if (!productId) {
      console.warn("No productId - cannot get cached variant");
      return null;
    }

    const directVariantId = selectedOption?.dataset.variantId;
    const directHit = directVariantId ? globalVariantsCache.get(`${productId}|id:${directVariantId}`) || null : null;

    const optionMap = this.#optionMap();
    if (!optionMap) return directHit;

    const { byPosition, contractOk, count } = this.#selectedByPosition();
    if (count === 0 || !contractOk) return directHit;

    const row = optionMap.find((r) => this.#rowMatchesExact(r, byPosition));
    if (row) return globalVariantsCache.get(`${productId}|id:${row.id}`) || null;

    // 정확 매칭 row가 없을 때 directHit(클릭된 옵션의 대표 variant) 폴백은
    // 선택이 "불완전"할 때만 유효하다(컬러만 선택·사이즈 deselect → 컬러 대표 variant).
    // 모든 옵션 축이 선택됐는데도(complete) 매칭 row가 없으면 그 조합은 존재하지 않는
    // (컬러 무관) 잘못된 사이즈 클릭이므로, directHit(다른 컬러 대표 variant)를 돌려주면
    // 엉뚱한 컬러가 form에 커밋된다. 이 경우엔 null을 반환한다. 호출부(variantChanged)가
    // 완결+null을 감지해 대표 variant를 fetch하지 않고 resource=null("Unavailable")로
    // dispatch하므로 잘못된 variant가 커밋되지 않는다(F-VP1).
    return this.isSelectionComplete ? null : directHit;
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
